import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';

import Icons from 'assets/icons';

interface AccordionItemProps {
  name: string;
  description: string;
  index: number;
  role?: string;
}

const AccordionItem = ({
  name,
  description,
  index,
  role,
}: AccordionItemProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const contentElement = contentRef.current;
    let resizeObserver: ResizeObserver | undefined;

    const updateHeight = () => {
      if (contentElement) {
        contentElement.style.height = isOpen
          ? `${contentElement.scrollHeight}px`
          : '0px';
      }
    };

    if (contentElement) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentElement);
    }

    const handleResize = () => {
      if (isOpen && contentElement) {
        contentElement.style.height = 'auto';
        const newScrollHeight = contentElement.scrollHeight;

        contentElement.style.height = `${newScrollHeight}px`;
      }
    };

    window.addEventListener('resize', handleResize);

    updateHeight();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div role={role}>
      <button
        type="button"
        id={`accordion-title-${index}`}
        className={classNames(
          'flex items-center justify-between w-full py-3 px-4 rounded-md font-medium border border-navy-200 gap-3',
          { 'border-b-0 bg-navy-50 rounded-b-none': isOpen }
        )}
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${index}`}
      >
        <span className="flex items-center gap-2">
          <span
            className={classNames(
              'flex items-center justify-center w-5 h-5 bg-navy-500 rounded-full text-white text-sm shrink-0',
              { 'bg-navy-800': isOpen }
            )}
          >
            {index + 1}
          </span>
          <span
            className={classNames(
              'text-gray-700 font-semibold text-left break-all',
              {
                'text-navy-800': isOpen,
              }
            )}
            title={name}
          >
            {name}
          </span>
        </span>
        <Icons.Arrow
          className={classNames('rotate-90 transition shrink-0', {
            '!-rotate-90': isOpen,
          })}
          aria-hidden="true"
        />
      </button>
      <div
        ref={contentRef}
        className={classNames('overflow-hidden transition-all', {
          'rounded-b-md': isOpen,
        })}
        role="region"
        aria-labelledby={`accordion-title-${index}`}
      >
        <div
          className={classNames({
            'p-5 border border-navy-200 border-t-0 rounded-b-md': isOpen,
            hidden: !isOpen,
          })}
          title={description}
        >
          <MarkdownComponent text={description} overrides={undefined} />
        </div>
      </div>
    </div>
  );
};

export default AccordionItem;
